import React from 'react';
import PropTypes from 'prop-types';
import {
  BookmarkIcon,
  ChatBubbleBottomCenterTextIcon,
  EnvelopeIcon,
  HomeIcon,
  UsersIcon,
  SparklesIcon,
  CheckCircleIcon,
  ChartBarIcon,
  DocumentCheckIcon,
  BookOpenIcon,
  CursorArrowRaysIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import NetworkLogos from '@/components/NetworkLogos';
import Account from './Account';
import useRouter from '@/hooks/useRouter';
import logo from '@assets/images/logos/cs_logo_long_format.png';
import classNames from 'classnames';
import { useConfig } from '@lore/config';
import NavigationLink from './NavigationLink';
import isActiveChecker from '@/utils/isActiveChecker';
import usePendingEvaluationsCount from './my-evaluations/usePendingEvaluationsCount';
import usePendingInvitationsCount from './my-invitations/usePendingInvitationsCount';
import useActiveInvitationsCount from './my-participating/useActiveInvitationsCount';
import usePendingExpiredCount from './my-expired/usePendingExpiredCount';
import useDraftOpportunityCount from './my-expired/useDraftOpportunityCount';
import useActiveOpportunityCount from './my-expired/useActiveOpportunityCount';
import ContactUs from './ContactUs';
import { useUser } from '@lore/auth';
import useNearbyNetworks from "@/hooks/useNearbyNetworks";
import EnvironmentBadge from "@/components/EnvironmentBadge";
import _ from 'lodash';

function NavigationLabel(props) {
  const { children } = props;

  return (
    <h3 className="text-cs-gray-900 text-xs font-semibold">
      {children}
    </h3>
  );
}

NavigationLabel.propTypes = {
  children: PropTypes.node.isRequired
};

function NavigationGroup(props) {
  const { children } = props;

  return (
    <div className="px-8 space-y-3">
      {children}
    </div>
  );
}

NavigationGroup.propTypes = {
  children: PropTypes.node.isRequired
};

export default function Navigation() {
  const config = useConfig();

  const currentUser = useUser();
  const { location } = useRouter();
  const currentRoute = location.pathname;

  const _createConnectCount = (
    useDraftOpportunityCount() +
    useActiveOpportunityCount() +
    useActiveInvitationsCount()
  );

  const createConnectCount = _.isNumber(_createConnectCount) ? _createConnectCount : '...';

  const home = [
    { name: 'Home', icon: HomeIcon, to: '/dashboard', startsWithOneOf: ['/dashboard'] },
    { name: 'Respond to Invites', icon: EnvelopeIcon, to: '/my-invitations', startsWithOneOf: ['/my-invitations'], count: usePendingInvitationsCount(), highlight: true },
    { name: 'Create and Connect', icon: CursorArrowRaysIcon, to: '/my-opportunities', startsWithOneOf: ['/my-opportunities', '/my-leading', '/my-participating'], count: createConnectCount, highlight: true },
    { name: 'Close Activities', icon: CheckCircleIcon, to: '/my-expired', startsWithOneOf: ['/my-expired', '/expired'], count: usePendingExpiredCount(), highlight: true },
    { name: 'Share Impact', icon: ChartBarIcon, to: '/my-evaluations', startsWithOneOf: ['/my-evaluations'], count: usePendingEvaluationsCount(), highlight: true },
    { name: 'See Past Activities', icon: DocumentCheckIcon, to: '/my-past', startsWithOneOf: ['/my-past'] },
    { name: 'View Conversations', icon: ChatBubbleBottomCenterTextIcon, to: '/messages', startsWithOneOf: ['/messages'] }
  ];

  // const myNavigation = [
  //   { name: 'Invitations', icon: EnvelopeIcon, to: '/my-invitations', startsWithOneOf: ['/my-invitations'], count: usePendingInvitationsCount(), highlight: true },
  //   // { name: 'Leading', icon: UserGroupIcon, to: '/my-leading', startsWithOneOf: ['/my-leading', '/leading'] },
  //   { name: 'Leading / Participating', icon: SparklesIcon, to: '/my-opportunities', startsWithOneOf: ['/my-opportunities', '/my-leading', 'my-participating'] },
  //   { name: 'Expired', icon: ClockIcon, to: '/my-expired', startsWithOneOf: ['/my-expired', '/expired'] },
  //   // { name: 'Participating', icon: CalendarIcon, to: '/my-participating', startsWithOneOf: ['/my-participating', '/participating'], count: useActiveInvitationsCount() },
  //   { name: 'Past', icon: ArchiveBoxIcon, to: '/my-past', startsWithOneOf: ['/my-past'] },
  //   { name: 'Evaluations', icon: ClipboardDocumentListIcon, to: '/my-evaluations', startsWithOneOf: ['/my-evaluations'], count: usePendingEvaluationsCount(), highlight: true }
  // ];

  const exploreNavigation = _.compact([
    { name: 'People', icon: UsersIcon, to: '/people', startsWithOneOf: ['/people', '/educators', '/partners'] },
    // { name: 'Partners', icon: UsersIcon, to: '/partners-populate', startsWithOneOf: ['/partners'] },
    // { name: 'Educators', icon: UsersIcon, to: '/educators-populate', startsWithOneOf: ['/educators'] },
    { name: 'Opportunities', icon: SparklesIcon, to: '/opportunities', startsWithOneOf: ['/projects', '/events', '/offers', '/opportunities'] },
    // { name: 'Inspiration (Past)', icon: ClockIcon, to: '/projects2-populate', startsWithOneOf: ['/projects'] },
    // { name: 'Offers', icon: NewspaperIcon, to: '/offers-populate', startsWithOneOf: ['/offers'] },
    { name: 'Idea Hub', icon: LightBulbIcon, to: '/idea-hub', startsWithOneOf: ['/idea-hub'] },
    { name: 'Bookmarks', icon: BookmarkIcon, to: '/bookmarks', startsWithOneOf: ['/bookmarks'] },
    currentUser.data.isStaff ?
      { name: '[Impact Stories]', icon: BookOpenIcon, to: '/stories', startsWithOneOf: ['/stories'] } :
      null
  ]);

  const networks = useNearbyNetworks();

  return (
    <div className="space-y-2 bg-white">
      <div className="mb-6">
        <div className="flex items-center justify-center gap-2">
          <div className="flex-shrink-0">
            <Link to="/" className="relative">
              <img
                className={classNames(
                  'h-14',
                  config.flags.grayscale && 'grayscale'
                )}
                src={logo}
                alt="logo"
              />
              <EnvironmentBadge className="absolute -translate-y-1/2 translate-x-20"/>
            </Link>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        <NavigationGroup>
          <NavigationLabel>
            Your Account
          </NavigationLabel>
          <div className="-mx-3">
            <Account />
          </div>
        </NavigationGroup>
        <NavigationGroup>
          <div className="space-y-1 mt-6">
            {home.map((item) => (
              <NavigationLink
                // key={item.name}
                // item={item}
                key={item.name}
                name={item.name}
                icon={item.icon}
                count={item.count}
                highlight={item.highlight}
                // onClick={() => history.push(item.to)}
                to={item.to}
                isActive={item.startsWithOneOf ?
                  isActiveChecker.startsWithOneOf(currentRoute, item.startsWithOneOf) :
                  isActiveChecker.startsWith(currentRoute, item.to)
                }
              />
            ))}
          </div>
        </NavigationGroup>
        {/*<NavigationGroup>*/}
        {/*  <NavigationLabel>*/}
        {/*    Your Stuff*/}
        {/*  </NavigationLabel>*/}
        {/*  <div className="space-y-1">*/}
        {/*    {myNavigation.map((item) => (*/}
        {/*      <NavigationLink*/}
        {/*        // key={item.name}*/}
        {/*        // item={item}*/}
        {/*        key={item.name}*/}
        {/*        name={item.name}*/}
        {/*        icon={item.icon}*/}
        {/*        count={item.count}*/}
        {/*        highlight={item.highlight}*/}
        {/*        // onClick={() => history.push(item.to)}*/}
        {/*        to={item.to}*/}
        {/*        isActive={item.startsWithOneOf ?*/}
        {/*          isActiveChecker.startsWithOneOf(currentRoute, item.startsWithOneOf) :*/}
        {/*          isActiveChecker.startsWith(currentRoute, item.to)*/}
        {/*        }*/}
        {/*      />*/}
        {/*    ))}*/}
        {/*  </div>*/}
        {/*</NavigationGroup>*/}
        <NavigationGroup className="flex-grow flex flex-col">
          <NavigationLabel>
            Explore
          </NavigationLabel>
          <div className="space-y-1">
            {exploreNavigation.map((item) => (
              <NavigationLink
                key={item.name}
                name={item.name}
                icon={item.icon}
                count={item.count}
                highlight={item.highlight}
                // onClick={() => history.push(item.to)}
                to={item.to}
                isActive={item.startsWithOneOf ?
                  isActiveChecker.startsWithOneOf(currentRoute, item.startsWithOneOf) :
                  isActiveChecker.startsWith(currentRoute, item.to)
                }
              />
            ))}
          </div>
        </NavigationGroup>
        {networks.data.length > 0 &&
          <NavigationGroup>
            <NavigationLabel>
              In partnership with
            </NavigationLabel>
            <NetworkLogos/>

          </NavigationGroup>
        }
        {config.intercom.enabled && (
          <NavigationGroup>
            <div className="pt-4">
              <ContactUs />
            </div>
          </NavigationGroup>
        )}
      </div>
    </div>
  )
}
